/**
 * Reviewer's inbox — contracts the actor has been asked to review.
 *
 * Contracts shown here are NOT widened into the regular Avtal list;
 * the only way to land on them is through this page or a direct link.
 * Clicking a row jumps to the editor; the policy module grants the
 * reviewer view + edit access for the lifetime of the open review.
 */

import { Link } from 'react-router-dom';
import { tenantApi } from '../api.js';
import { useTenantQuery } from '../query.js';
import { usePageTitle } from '../use-page-title.js';

export function ReviewsPage() {
  usePageTitle('Granskningar');
  const { data, error, isLoading } = useTenantQuery(
    'reviews.list_my_pending',
    () => tenantApi.reviews.list_my_pending(),
  );
  const reviews = data?.reviews ?? null;

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium text-stone-500">Mina granskningar</h2>
      {error && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {(error as Error).message}
        </div>
      )}
      {isLoading || reviews === null ? (
        <div className="text-sm text-stone-400">loading…</div>
      ) : reviews.length === 0 ? (
        <div className="rounded border border-dashed border-stone-300 p-6 text-center text-sm text-stone-500">
          Inga avtal väntar på din granskning.
        </div>
      ) : (
        <ul className="divide-y rounded border">
          {reviews.map((r) => (
            <li key={r.reviewId} className="hover:bg-stone-50">
              <Link to={`/contracts/${r.contractId}/edit`} className="block p-3">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-medium">{r.title}</span>
                  <span className="rounded bg-purple-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-purple-800">
                    Granska
                  </span>
                </div>
                <div className="mt-1 text-xs text-stone-500">
                  {r.templateName} · skickad av{' '}
                  {r.requester.name ?? r.requester.email ?? 'okänd'} ·{' '}
                  {new Date(r.requestedAt).toLocaleString('sv-SE')}
                </div>
                {r.requestComment && (
                  <div className="mt-1 line-clamp-2 text-xs text-stone-700">
                    {r.requestComment}
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
